import React, { useState, useEffect } from "react";
import patientService from "../../../Services/patientService";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import expenseService from "../../../Services/expenseService";
import expenseHeadService from "../../../Services/expenseHeadService";
import { FaArrowLeftLong } from "react-icons/fa6";
import { FaArrowRightLong } from "react-icons/fa6";
const SectionLabel = ({ children }) => (
  <p className="text-[11px] uppercase tracking-widest font-[500] text-gray-400 mb-3 mt-6">
    {children}
  </p>
);

const InputField = ({ label, prefix, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[12px] font-[500] text-gray-500">{label}</label>
    <div className="relative">
      {prefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-gray-400">
          {prefix}
        </span>
      )}
      <input
        {...props}
        className={`w-full ${prefix ? "pl-8" : "px-3"} pr-3 py-2 text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none hover:border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100`}
      />
    </div>
  </div>
);

const SelectField = ({ label, children, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[12px] font-[500] text-gray-500">{label}</label>
    <select
      {...props}
      className="w-full px-3 py-2 text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none hover:border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
    >
      {children}
    </select>
  </div>
);

const AddEditExpense = () => {
  const [patientData, setPatientData] = useState({
    item_name: "",
    amount: "",
    expense_date: "",
    description: "",
    title: "",
  });

  const [expenseHeadData, setExpenseHeadData] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      const fetchPatientData = async () => {
        try {
          const response = await patientService.fetchPatientById(id);
          setPatientData(response.patient);
        } catch (error) {
          toast.error("Error fetching patient data.");
        }
      };
      fetchPatientData();
    }
  }, [id]);

  useEffect(() => {
    const getExpenseHead = async () => {
      try {
        const response = await expenseHeadService.fetchAll();
        setExpenseHeadData(response.expensehead);
      } catch (error) {
        toast.error("Error fetching expense heads.");
      }
    };
    getExpenseHead();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPatientData({ ...patientData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (id) {
        await expenseService.update(id, patientData);
        toast.success("Expense updated successfully!");
      } else {
        await expenseService.create(patientData);
        toast.success("Expense added successfully!");
      }
      navigate("/account/expense");
    } catch (error) {
      toast.error("Error saving expense.");
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen flex items-start justify-center py-6 sm:py-10 px-3 sm:px-4">
      <div className="w-full max-w-[1400px]">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

          <div className="bg-[#2F6DB4] px-5 sm:px-8 py-4 sm:py-5">
            <h1 className="text-white text-[16px] sm:text-[18px] font-[500] tracking-tight">
              {id ? "Edit Expense" : "Add Expense"}
            </h1>
            <p className="text-blue-200 text-[11px] sm:text-[12px] mt-0.5">
              Fill in all fields to register the expense record
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-5 sm:px-8 py-5 sm:py-6">

              <SectionLabel>Basic Information</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="Item Name"
                  type="text"
                  name="item_name"
                  placeholder="e.g. Office Supplies"
                  value={patientData.item_name}
                  onChange={handleChange}
                  required
                />
                <InputField
                  label="Expense Date"
                  type="date"
                  name="expense_date"
                  value={patientData.expense_date}
                  onChange={handleChange}
                  required
                />
              </div>

              <SectionLabel>Financial Details</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="Amount"
                  type="number"
                  name="amount"
                  placeholder="0"
                  prefix="Rs."
                  value={patientData.amount}
                  onChange={handleChange}
                  required
                />
                <SelectField
                  label="Expense Account Head"
                  name="title"
                  value={patientData.title}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Select Account Head --</option>
                  {expenseHeadData.map((expensehead) => (
                    <option key={expensehead.id} value={expensehead.id}>
                      {expensehead.title}
                    </option>
                  ))}
                </SelectField>
              </div>

              <SectionLabel>Additional Details</SectionLabel>
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-[500] text-gray-500">Description</label>
                <textarea
                  name="description"
                  placeholder="Optional notes about this expense..."
                  value={patientData.description}
                  onChange={handleChange}
                  required
                  rows={3}
                  className="w-full px-3 py-2 text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none hover:border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 resize-none"
                />
              </div>

              <div className="border-t border-gray-100 mt-8 pt-5 flex flex-col-reverse sm:flex-row items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/account/expense")}
                  className="w-full sm:w-auto text-[13px] text-gray-400 hover:text-gray-600 transition-colors font-[500] text-center py-2"
                >
                 <FaArrowLeftLong className="inline mb-0.5" />  Cancel
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto relative overflow-hidden bg-[#2F6DB4] text-white text-[13px] font-[500] px-6 py-2.5 rounded-lg border border-gray-300 shadow-md hover:shadow-gray-200 hover:scale-[1.03] active:scale-[0.97] transition-all duration-300 before:absolute before:inset-0 before:-translate-x-full hover:before:translate-x-full before:bg-white/20 before:transition-transform before:duration-500 flex items-center justify-center gap-2"
                >
                  {id ? (
                    "Update Expense →"
                  ) : (
                    <>
                      Save Expense <FaArrowRightLong />
                    </>
                  )}
                </button>
              </div>

            </div>
          </form>
        </div>

        <p className="text-center text-[11px] sm:text-[12px] text-gray-400 mt-4 px-2">
          All fields marked as required must be filled before saving.
        </p>
      </div>
    </div>
  );
};

export default AddEditExpense;